import { NOTIFICATION_TYPE_LABELS, UNREAD_BADGE_CAP, type NotificationSummary } from '@whn/shared';
import { Bell, CheckCheck } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, formatRelative } from '@/lib/utils';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationsRead,
  useNotificationCounts,
  useNotifications,
} from './use-notifications';

export function NotificationsMenu() {
  const [open, setOpen] = React.useState(false);
  const counts = useNotificationCounts();
  // Only fetched once the menu is opened: the badge needs a count, not a list.
  const notifications = useNotifications(open ? { pageSize: 15 } : {});
  const markAllRead = useMarkAllNotificationsRead();

  const unread = counts.data?.unread ?? 0;
  const label =
    unread === 0
      ? 'Notifications'
      : `Notifications, ${unread > UNREAD_BADGE_CAP ? `more than ${UNREAD_BADGE_CAP}` : unread} unread`;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} className="relative">
          <Bell aria-hidden="true" />
          {unread > 0 ? (
            <span
              // The count is in the button's accessible name already; this is
              // the visual half and would otherwise be read out twice.
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white"
            >
              {unread > UNREAD_BADGE_CAP ? `${UNREAD_BADGE_CAP}+` : unread}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
          <h2 className="text-sm font-semibold text-ink">Notifications</h2>
          {unread > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              isLoading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              <CheckCheck className="size-3.5" aria-hidden="true" />
              Mark all read
            </Button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notifications.isPending ? (
            <p className="px-3 py-6 text-center text-sm text-ink-muted" role="status">
              Loading…
            </p>
          ) : null}

          {notifications.data?.data.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-ink-muted">
              Nothing yet. Approvals and workflow alerts land here.
            </p>
          ) : null}

          <ul>
            {notifications.data?.data.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </ul>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  notification,
  onNavigate,
}: {
  notification: NotificationSummary;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const markRead = useMarkNotificationsRead();
  const isUnread = notification.readAt === null;

  const open = (): void => {
    if (isUnread) markRead.mutate([notification.id]);
    if (notification.link) {
      onNavigate();
      navigate(notification.link);
    }
  };

  return (
    <li className="border-b border-border-subtle last:border-b-0">
      <button
        type="button"
        onClick={open}
        className={cn(
          'flex w-full gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken',
          isUnread && 'bg-brand-soft/40',
        )}
      >
        <span
          className={cn(
            'mt-1.5 size-2 shrink-0 rounded-full',
            isUnread ? 'bg-brand' : 'bg-transparent',
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium uppercase tracking-wide text-ink-subtle">
            {NOTIFICATION_TYPE_LABELS[notification.type]}
            {isUnread ? <span className="sr-only"> (unread)</span> : null}
          </span>
          <span className="block text-sm font-medium text-ink">{notification.title}</span>
          {notification.body ? (
            <span className="block truncate text-xs text-ink-muted">{notification.body}</span>
          ) : null}
          <span className="block text-xs text-ink-subtle">
            {formatRelative(notification.createdAt)}
          </span>
        </span>
      </button>
    </li>
  );
}
